// pages/settings/WebexUserSettings.jsx
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  RefreshCw,
  Save,
  Users,
  Lock,
  ShieldCheck,
  Settings,
  ChevronDown,
  ChevronUp,
  Search,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";

const PROCESSING_TYPES = {
  lead_qualification: {
    label: "Lead Qualification",
    description: "Full AI pipeline with qualification scoring",
    color: "text-green-400",
    bg: "bg-green-600/20",
  },
  sales_call: {
    label: "Sales Call Processing",
    description: "Sales-focused pipeline with CRM integration",
    color: "text-blue-400",
    bg: "bg-blue-600/20",
  },
  audio_only: {
    label: "Audio Storage Only",
    description: "Store recording without AI processing",
    color: "text-yellow-400",
    bg: "bg-yellow-600/20",
  },
  disabled: {
    label: "Disabled",
    description: "Skip this user's recordings",
    color: "text-red-400",
    bg: "bg-red-600/20",
  },
};

function WebexUserConfigRow({ user, onUpdate, isSaving }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localConfig, setLocalConfig] = useState({
    userDisplayName: user.userDisplayName || "",
    processingType: user.processingType || "lead_qualification",
    isActive: user.isActive ?? true,
    enablePasswordProtection: user.enablePasswordProtection || false,
    mappedUserId: user.mappedUserId || null,
    processingSettings: user.processingSettings || {},
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (field, value) => {
    setLocalConfig((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(user.webexUserEmail, localConfig);
    setHasChanges(false);
  };

  const handleSetPassword = async () => {
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await api.setWebexUserDefaultPassword(
        user.webexUserEmail,
        password,
        applyToExisting,
      );
      toast.success("Default password set successfully");
      setShowPasswordModal(false);
      setPassword("");
      setApplyToExisting(false);

      // Enable password protection
      handleChange("enablePasswordProtection", true);
    } catch (error) {
      toast.error("Failed to set password");
      console.error(error);
    }
  };

  const typeInfo = PROCESSING_TYPES[localConfig.processingType];

  return (
    <>
      <div
        className={`border rounded-lg transition-all ${
          hasChanges ? "border-purple-500/50" : "border-gray-700"
        } bg-[#1a1d23]`}
      >
        {/* Row header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#22252b] transition-colors rounded-lg"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-600/20 border border-purple-600/30 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-purple-400" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium truncate">
                  {localConfig.userDisplayName ||
                    user.webexDisplayName ||
                    user.webexUserEmail}
                </span>
                {localConfig.enablePasswordProtection && (
                  <Lock className="h-3.5 w-3.5 text-yellow-500" />
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs text-gray-500">
                  {user.webexUserEmail}
                </span>
                <span
                  className={`text-xs flex items-center gap-1 ${typeInfo.color}`}
                >
                  {typeInfo.label}
                </span>
                {user.callCount > 0 && (
                  <span className="text-xs text-gray-500">
                    {user.callCount} calls
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {hasChanges && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                Save
              </button>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded configuration */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-700/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Display Name */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={localConfig.userDisplayName}
                  onChange={(e) =>
                    handleChange("userDisplayName", e.target.value)
                  }
                  placeholder={user.webexDisplayName || user.webexUserEmail}
                  className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Processing Type */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Processing Pipeline
                </label>
                <select
                  value={localConfig.processingType}
                  onChange={(e) =>
                    handleChange("processingType", e.target.value)
                  }
                  className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                >
                  {Object.entries(PROCESSING_TYPES).map(([key, info]) => (
                    <option key={key} value={key}>
                      {info.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Password Protection Section */}
            <div className="mt-4 p-4 bg-yellow-900/10 border border-yellow-800/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-yellow-400" />
                  <h4 className="text-sm font-medium text-yellow-300">
                    Password Protection
                  </h4>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConfig.enablePasswordProtection}
                    onChange={(e) =>
                      handleChange("enablePasswordProtection", e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-yellow-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                When enabled, all new recordings from this user will be
                automatically password protected
              </p>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded-lg transition-colors"
              >
                <Lock className="h-3 w-3" />
                Set Default Password
              </button>
            </div>

            {/* Active toggle */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConfig.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
                <span className="text-sm text-gray-300">Active</span>
              </div>

              {hasChanges && (
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Changes
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Set Default Password</h3>
            <p className="text-sm text-gray-400 mb-4">
              Set a default password for all recordings from{" "}
              {user.webexUserEmail}
            </p>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password (min 6 characters)"
                  autoComplete="new-password"
                  className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 pr-10 text-white text-sm focus:outline-none focus:border-purple-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyToExisting}
                  onChange={(e) => setApplyToExisting(e.target.checked)}
                  className="rounded border-gray-600 bg-[#0C0E12] text-purple-500 focus:ring-purple-500"
                />
                Apply to existing unprotected recordings
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword("");
                  setApplyToExisting(false);
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSetPassword}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Set Password
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WebexUserSettings() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (autoDiscover = false) => {
    try {
      setIsLoading(true);
      const response = await api.getWebexUsers(autoDiscover);
      setUsers(response.users || []);
    } catch (error) {
      console.error("Failed to fetch Webex users:", error);
      toast.error("Failed to load Webex users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscoverUsers = async () => {
    try {
      setIsDiscovering(true);
      const result = await api.discoverWebexUsers();
      toast.success(`Discovered ${result.discovered} Webex users`);
      await fetchUsers();
    } catch (error) {
      console.error("Failed to discover users:", error);
      toast.error("Failed to discover Webex users");
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleUpdateConfig = async (webexUserEmail, configData) => {
    try {
      setIsSaving(true);
      await api.updateWebexUserConfig(webexUserEmail, configData);
      toast.success(`Configuration saved for ${webexUserEmail}`);
      await fetchUsers();
    } catch (error) {
      console.error("Failed to save config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !searchTerm ||
      user.webexUserEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.webexDisplayName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (user.userDisplayName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterType === "all" ||
      (filterType === "active" && user.isActive) ||
      (filterType === "inactive" && !user.isActive) ||
      (filterType === "protected" && user.enablePasswordProtection) ||
      user.processingType === filterType;

    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading Webex users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/settings?tab=integrations")}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Integrations
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <Users className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">
                  Webex User Configuration
                </h1>
                <p className="text-gray-400 text-sm mt-1">
                  Configure processing and security for Webex users
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDiscoverUsers}
                disabled={isDiscovering}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isDiscovering
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isDiscovering ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Users className="h-4 w-4" />
                )}
                Discover
              </button>
              <button
                onClick={() => fetchUsers()}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg pl-10 pr-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="protected">Password Protected</option>
              <option value="lead_qualification">Lead Qualification</option>
              <option value="sales_call">Sales Call</option>
              <option value="audio_only">Audio Only</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        {/* User list */}
        {users.length === 0 ? (
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-8 text-center">
            <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">
              No Webex users found. Click "Discover" to find users from
              recordings.
            </p>
            <button
              onClick={handleDiscoverUsers}
              disabled={isDiscovering}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg mx-auto transition-colors disabled:opacity-50"
            >
              {isDiscovering ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Users className="h-4 w-4" />
              )}
              Discover Users
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredUsers.length === 0 ? (
              <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-8 text-center">
                <Users className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No users match your filters</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <WebexUserConfigRow
                  key={user.webexUserEmail}
                  user={user}
                  onUpdate={handleUpdateConfig}
                  isSaving={isSaving}
                />
              ))
            )}
          </div>
        )}

        {/* Footer info */}
        {users.length > 0 && (
          <div className="mt-6 text-xs text-gray-500 text-center">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        )}
      </div>
    </div>
  );
}

export default WebexUserSettings;
